import { useEffect, useState } from 'react';
import { Badge, Button, Input, Label, Select, Sheet, Textarea } from '@nexply/ui';
import type { VCardContact } from '@nexply/core';
import type {
  ExtractedLink,
  ExtractedMember,
  MemberCreateInput,
  MemberDTO,
  RelationshipType,
} from '@nexply/api-client';
import { Network, Sparkles, Undo2 } from 'lucide-react';
import { ContactVoiceNote } from './ContactVoiceNote.js';

type MemberStatus = MemberDTO['status'];

const TYPE_LABEL: Record<RelationshipType, string> = {
  COLLEAGUE: 'Kolleg:in',
  CUSTOMER: 'Kunde',
  SUPPLIER: 'Lieferant',
  PARTNER: 'Partner',
  INVESTOR: 'Investor',
  FRIEND: 'Freund:in',
  OTHER: 'Sonstige',
};

/** Die Formularfelder, die Nex befüllen darf. */
type AiField =
  | 'company'
  | 'role'
  | 'industry'
  | 'city'
  | 'country'
  | 'email'
  | 'phone'
  | 'website'
  | 'offering'
  | 'seeking'
  | 'tags'
  | 'notes';

/**
 * Der Contract verlangt Vor- UND Nachname (je min. 1 Zeichen). vCards liefern oft nur ein
 * FN-Feld ("Anna Meier") oder gar nur einen Vornamen — hier wird nur vorgeschlagen, was
 * sicher ableitbar ist. Fehlt ein Teil, bleibt er leer und der Nutzer muss ihn ausfüllen.
 */
export function splitName(c: VCardContact): { firstName: string; lastName: string } {
  const first = (c.firstName ?? '').trim();
  const last = (c.lastName ?? '').trim();
  if (first && last) return { firstName: first, lastName: last };

  const parts = c.name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      firstName: first || parts[0]!,
      lastName: last || parts.slice(1).join(' '),
    };
  }
  return { firstName: first || parts[0] || '', lastName: last };
}

export interface EnrichResult {
  input: Omit<MemberCreateInput, 'photoUrl'>;
  /** true = "Nur übernehmen": ohne die Matching-Felder speichern. */
  quick: boolean;
  /** Vom Nutzer bestätigte KI-Vorschläge — werden erst nach dem Anlegen zu Kanten. */
  links: ExtractedLink[];
}

export interface ContactEnrichSheetProps {
  open: boolean;
  contact: VCardContact | null;
  pending: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (result: EnrichResult) => void;
}

export function ContactEnrichSheet({
  open,
  contact,
  pending,
  error,
  onCancel,
  onSubmit,
}: ContactEnrichSheetProps) {
  const [form, setForm] = useState(() => emptyForm());
  const [aiFields, setAiFields] = useState<Set<AiField>>(() => new Set());
  // Welche Tags Nex hinzugefügt hat — "Verwerfen" darf nur diese entfernen,
  // nicht die aus der vCard oder vom Nutzer selbst getippte.
  const [aiTags, setAiTags] = useState<string[]>([]);
  const [links, setLinks] = useState<ExtractedLink[]>([]);
  // Über die memberId geschlüsselt: Indizes verschieben sich, wenn ein zweiter
  // KI-Lauf Links dazumergt.
  const [confirmed, setConfirmed] = useState<Set<string>>(() => new Set());
  const [applied, setApplied] = useState(false);
  const [session, setSession] = useState(0);

  // Das Sheet wird für jeden Kontakt wiederverwendet — ohne Reset schleppt die nächste
  // Karte die KI-Ergebnisse der vorigen mit. `session` als key setzt auch die Sprachnotiz
  // (Transkript, Aufnahme) zurück, deren State in der Kindkomponente liegt.
  useEffect(() => {
    if (!contact) return;
    setForm(fromContact(contact));
    setAiFields(new Set());
    setAiTags([]);
    setLinks([]);
    setConfirmed(new Set());
    setApplied(false);
    setSession((s) => s + 1);
  }, [contact]);

  const upd = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Sobald der Nutzer selbst tippt, ist das Feld seins — nicht mehr "von Nex".
    // Das Formularfeld heißt `tagsRaw`, das KI-Flag `tags` — ohne das Mapping
    // blieb der "von Nex"-Badge auf den Tags für immer hängen.
    const flag = (k === 'tagsRaw' ? 'tags' : k) as AiField;
    setAiFields((prev) => {
      if (!prev.has(flag)) return prev;
      const next = new Set(prev);
      next.delete(flag);
      return next;
    });
    if (flag === 'tags') setAiTags([]);
  };

  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim() || null;
  const nameOk = firstName.length > 0;

  /**
   * Ergänzen, nie ersetzen: Nex schreibt nur in Felder, die gerade leer sind — Notizen
   * werden angehängt statt verworfen (vCards bringen oft schon ein NOTE-Feld mit).
   * Läuft als funktionaler Updater: während des mehrsekündigen KI-Aufrufs darf der
   * Nutzer weitertippen, ohne dass seine Eingaben von einem alten Snapshot
   * überschrieben werden.
   */
  function applyExtracted(ex: ExtractedMember) {
    const marked = new Set<AiField>();
    let addedTags: string[] = [];

    setForm((current) => {
      const next = { ...current };

      const put = (key: Exclude<AiField, 'tags' | 'notes'>, value: string | null) => {
        const v = (value ?? '').trim();
        if (!v || next[key].trim()) return;
        next[key] = v;
        marked.add(key);
      };

      put('company', ex.company);
      put('role', ex.role);
      put('industry', ex.industry);
      put('city', ex.city);
      put('country', ex.country);
      put('email', ex.email);
      put('phone', ex.phone);
      put('website', ex.website);
      put('offering', ex.offering);
      put('seeking', ex.seeking);

      // Notizen anhängen statt blockieren — die KI-Notiz ist oft der wichtigste Teil.
      const note = (ex.notes ?? '').trim();
      if (note && !next.notes.includes(note)) {
        next.notes = next.notes.trim() ? `${next.notes.trim()}\n\n${note}` : note;
        marked.add('notes');
      }

      const existing = splitTags(next.tagsRaw);
      const seen = new Set(existing.map((t) => t.toLowerCase()));
      addedTags = ex.tags.map((t) => t.trim()).filter((t) => t && !seen.has(t.toLowerCase()));
      if (addedTags.length > 0) {
        next.tagsRaw = [...existing, ...addedTags].join(', ');
        marked.add('tags');
      }

      return next;
    });

    // Vereinigen statt ersetzen: beim zweiten Auswerten bleiben die Markierungen des
    // ersten Laufs bestehen. `upd` nimmt selbst getippte Felder ohnehin raus.
    setAiFields((prev) => new Set([...prev, ...marked]));
    setAiTags((prev) => [...prev, ...addedTags]);
    // Links per memberId dazumergen — ein zweiter Lauf darf bestätigte Verbindungen
    // aus dem ersten nicht wegwerfen.
    setLinks((prev) => {
      const known = new Set(prev.map((l) => l.memberId));
      return [...prev, ...ex.links.filter((l) => !known.has(l.memberId))];
    });
    setConfirmed((prev) => new Set([...prev, ...ex.links.map((l) => l.memberId)]));
    setApplied(true);
  }

  /** Verwirft nur, was Nex geschrieben hat — selbst getippte Felder bleiben unangetastet. */
  function discardAi() {
    setForm((f) => {
      const next = { ...f };
      for (const k of aiFields) {
        if (k === 'tags') {
          const drop = new Set(aiTags.map((t) => t.toLowerCase()));
          next.tagsRaw = splitTags(next.tagsRaw)
            .filter((t) => !drop.has(t.toLowerCase()))
            .join(', ');
        } else {
          next[k] = '';
        }
      }
      return next;
    });
    setAiFields(new Set());
    setAiTags([]);
    setLinks([]);
    setConfirmed(new Set());
    setApplied(false);
  }

  const chosenLinks = links.filter((l) => confirmed.has(l.memberId));

  /**
   * Enthält bewusst ALLE Felder: "Nur übernehmen" hat früher Branche, Tags, Angebot
   * und Gesuch stumm verworfen — genau die Felder, die die Sprachnotiz liefert.
   * Der Unterschied der beiden Buttons ist nur noch der Weg, nicht die Datenmenge.
   */
  function base(): Omit<MemberCreateInput, 'photoUrl'> {
    return {
      firstName,
      lastName,
      company: form.company.trim() || null,
      role: form.role.trim() || null,
      industry: form.industry.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      website: form.website.trim() || null,
      city: form.city.trim() || null,
      country: form.country.trim() || null,
      tags: splitTags(form.tagsRaw),
      notes: form.notes.trim() || null,
      offering: form.offering.trim() || null,
      seeking: form.seeking.trim() || null,
      status: form.status,
    };
  }

  function submitFull() {
    onSubmit({ quick: false, links: chosenLinks, input: base() });
  }

  return (
    <Sheet
      open={open}
      onClose={onCancel}
      title="Ins Netzwerk übernehmen"
      description={contact?.name}
      variant="full"
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button
            variant="secondary"
            fullWidth
            disabled={!nameOk || pending}
            onClick={() => onSubmit({ quick: true, links: chosenLinks, input: base() })}
          >
            Nur übernehmen
          </Button>
          <Button fullWidth disabled={!nameOk || pending} loading={pending} onClick={submitFull}>
            Speichern
          </Button>
        </div>
      }
    >
      <div className="space-y-5 pb-2">
        {!nameOk && (
          <div className="rounded-xl border border-gold/30 bg-gold/10 p-3 text-xs text-text-dim">
            Der Vorname ist Pflicht. Im Handy-Kontakt fehlt er — bitte kurz ergänzen.
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-danger/30 bg-danger/10 p-3 text-xs text-danger">
            {error}
          </div>
        )}

        <ContactVoiceNote
          key={session}
          active={open}
          applied={applied}
          known={{
            name: `${firstName} ${lastName}`.trim() || undefined,
            company: form.company.trim() || null,
            role: form.role.trim() || null,
            city: form.city.trim() || null,
          }}
          onExtracted={applyExtracted}
        />

        {applied && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gold/25 bg-gold/[0.06] px-3 py-2">
            <span className="text-xs text-text-dim">
              {aiFields.size > 0
                ? `Nex hat ${aiFields.size} ${aiFields.size === 1 ? 'Feld' : 'Felder'} ergänzt — unten markiert. Alles bleibt änderbar.`
                : 'Nex konnte nichts Neues ergänzen — deine Angaben waren schon vollständig.'}
            </span>
            <Button variant="ghost" size="sm" onClick={discardAi}>
              <Undo2 className="h-3.5 w-3.5" /> Ergänzung verwerfen
            </Button>
          </div>
        )}

        {links.length > 0 && (
          <div className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gold-light">
              <Network className="h-4 w-4" />
              Erkannte Verbindungen
            </div>
            <p className="text-xs text-text-dim">
              Hak an, was wirklich stimmt. Bestätigte Verbindungen legt Nex nach dem Speichern an —
              sie hängen dann direkt an den beteiligten Mitgliedern.
            </p>
            {links.map((l) => (
              <label
                key={l.memberId}
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-line bg-surface-2 p-3"
              >
                <input
                  type="checkbox"
                  checked={confirmed.has(l.memberId)}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setConfirmed((prev) => {
                      const next = new Set(prev);
                      if (on) next.add(l.memberId);
                      else next.delete(l.memberId);
                      return next;
                    });
                  }}
                  className="mt-1 h-4 w-4 shrink-0 accent-gold"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-text">kennt {l.memberName}</span>
                    <Badge tone="gold">{TYPE_LABEL[l.type]}</Badge>
                  </span>
                  <span className="mt-1 block text-xs italic text-text-dim">
                    „{l.reason}“
                  </span>
                </span>
              </label>
            ))}
            <p className="text-[11px] text-text-muted">
              {chosenLinks.length} von {links.length} bestätigt.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Vorname *</Label>
            <Input value={form.firstName} onChange={(e) => upd('firstName', e.currentTarget.value)} />
          </div>
          <div>
            <Label>Nachname</Label>
            <Input value={form.lastName} onChange={(e) => upd('lastName', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('company')}>Firma</FieldLabel>
            <Input value={form.company} onChange={(e) => upd('company', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('role')}>Position</FieldLabel>
            <Input value={form.role} onChange={(e) => upd('role', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('email')}>E-Mail</FieldLabel>
            <Input type="email" value={form.email} onChange={(e) => upd('email', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('phone')}>Telefon</FieldLabel>
            <Input value={form.phone} onChange={(e) => upd('phone', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('website')}>Website</FieldLabel>
            <Input value={form.website} onChange={(e) => upd('website', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('city')}>Stadt</FieldLabel>
            <Input value={form.city} onChange={(e) => upd('city', e.currentTarget.value)} />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('country')}>Land</FieldLabel>
            <Input value={form.country} onChange={(e) => upd('country', e.currentTarget.value)} />
          </div>
          <div>
            <Label>Status</Label>
            <Select
              value={form.status}
              onChange={(e) => upd('status', e.currentTarget.value as MemberStatus)}
            >
              <option value="NEW">Neu</option>
              <option value="ACTIVE">Aktiv</option>
              <option value="ARCHIVED">Archiviert</option>
            </Select>
          </div>
        </div>

        <div className="rounded-2xl border border-gold/20 bg-gold/[0.06] p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gold-light">
            <Sparkles className="h-4 w-4" />
            Fürs Matching
          </div>
          <p className="text-xs text-text-dim">
            Branche, Tags, Angebot und Gesuch sind genau die Felder, auf denen die KI-Matches laufen.
            Je mehr hier steht, desto besser die Vorschläge.
          </p>
          <div>
            <FieldLabel ai={aiFields.has('industry')}>Branche</FieldLabel>
            <Input
              value={form.industry}
              onChange={(e) => upd('industry', e.currentTarget.value)}
              placeholder="z.B. Handwerk, Software, Beratung"
            />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('tags')}>Tags (kommagetrennt)</FieldLabel>
            <Input
              value={form.tagsRaw}
              onChange={(e) => upd('tagsRaw', e.currentTarget.value)}
              placeholder="z.B. B2B, Empfehlung, Bau"
            />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('offering')}>Was bietet die Person an?</FieldLabel>
            <Textarea
              value={form.offering}
              onChange={(e) => upd('offering', e.currentTarget.value)}
              placeholder="Leistungen, Produkte, Kontakte …"
            />
          </div>
          <div>
            <FieldLabel ai={aiFields.has('seeking')}>Was sucht sie?</FieldLabel>
            <Textarea
              value={form.seeking}
              onChange={(e) => upd('seeking', e.currentTarget.value)}
              placeholder="Kunden, Partner, Lieferanten …"
            />
          </div>
        </div>

        <div>
          <FieldLabel ai={aiFields.has('notes')}>Notizen</FieldLabel>
          <Textarea
            value={form.notes}
            onChange={(e) => upd('notes', e.currentTarget.value)}
            placeholder="Woher kennt ihr euch?"
          />
        </div>
      </div>
    </Sheet>
  );
}

/** Markiert sichtbar, was von Nex stammt — und was der Nutzer selbst getippt hat. */
function FieldLabel({ ai, children }: { ai: boolean; children: React.ReactNode }) {
  return (
    <Label>
      <span className="inline-flex items-center gap-2">
        {children}
        {ai && (
          <Badge tone="gold" className="normal-case tracking-normal">
            von Nex ergänzt
          </Badge>
        )}
      </span>
    </Label>
  );
}

function splitTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function emptyForm() {
  return {
    firstName: '',
    lastName: '',
    company: '',
    role: '',
    industry: '',
    email: '',
    phone: '',
    website: '',
    city: '',
    country: '',
    tagsRaw: '',
    notes: '',
    offering: '',
    seeking: '',
    status: 'NEW' as MemberStatus,
  };
}

function fromContact(c: VCardContact) {
  const { firstName, lastName } = splitName(c);
  return {
    ...emptyForm(),
    firstName,
    lastName,
    company: c.company ?? '',
    role: c.role ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    website: c.website ?? '',
    city: c.city ?? '',
    country: c.country ?? '',
    notes: c.notes ?? '',
  };
}
